import { useCallback } from 'react';

import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';

import { WorkloadsCostReportFilters } from '@cast/types';
import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';

const CLUSTER_DATA_TRANSFER_SUMMARY_QUERY_KEY = 'cluster-data-transfer-summary';

type Props = {
  clusterId: string;
  startTime: string;
  endTime: string;
  filter?: WorkloadsCostReportFilters;
  enabled?: boolean;
};

const getClusterDataTransferSummaryQueryKey = ({
  clusterId,
  startTime,
  endTime,
  filter,
}: Partial<
  Pick<Props, 'clusterId' | 'startTime' | 'endTime' | 'filter'>
> = {}) => {
  if (startTime === undefined) {
    return [CLUSTER_DATA_TRANSFER_SUMMARY_QUERY_KEY] as const;
  }

  return [
    CLUSTER_DATA_TRANSFER_SUMMARY_QUERY_KEY,
    clusterId,
    startTime,
    endTime,
    filter,
  ] as const;
};

const getClusterDataTransferSummaryQueryOptions = ({
  clusterId,
  startTime,
  endTime,
  filter = {},
  enabled = true,
}: Props) =>
  queryOptions({
    queryKey: getClusterDataTransferSummaryQueryKey({
      clusterId,
      startTime,
      endTime,
      filter,
    }),
    queryFn: async () => {
      const { data } = await apiClient.costReport.getClusterDataTransferSummary(
        {
          clusterId,
          startTime,
          endTime,
          filter,
        }
      );

      return data;
    },
    enabled: makeIsEnabled(Boolean(clusterId && startTime && endTime), enabled),
    refetchOnWindowFocus: false,
  });

export const useClusterDataTransferSummaryQuery = (props: Props) => {
  const queryClient = useQueryClient();
  const result = useQuery(getClusterDataTransferSummaryQueryOptions(props));

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: getClusterDataTransferSummaryQueryKey(),
    });
  }, [queryClient]);

  return {
    ...result,
    invalidate,
  };
};
